import { useState, useMemo } from "react";
import * as LucideIcons from "lucide-react";
import { Search, X } from "lucide-react";

// Curated list of real estate relevant icons
const AMENITY_ICONS = [
    // Pool & Water
    { name: "Waves", category: "Pool & Water" },
    { name: "Droplets", category: "Pool & Water" },

    // Fitness & Sports
    { name: "Dumbbell", category: "Fitness" },
    { name: "Bike", category: "Fitness" },
    { name: "Footprints", category: "Fitness" },

    // Security & Safety
    { name: "Shield", category: "Security" },
    { name: "ShieldCheck", category: "Security" },
    { name: "Lock", category: "Security" },
    { name: "Eye", category: "Security" },
    { name: "Camera", category: "Security" },

    // Parking & Transport
    { name: "Car", category: "Parking" },
    { name: "ParkingCircle", category: "Parking" },
    { name: "Bike", category: "Parking" },

    // Nature & Outdoors
    { name: "Trees", category: "Nature" },
    { name: "TreePine", category: "Nature" },
    { name: "Flower2", category: "Nature" },
    { name: "Sun", category: "Nature" },
    { name: "Mountain", category: "Nature" },

    // Social & Entertainment
    { name: "Building2", category: "Social" },
    { name: "Users", category: "Social" },
    { name: "PartyPopper", category: "Social" },
    { name: "Gamepad2", category: "Social" },
    { name: "Tv", category: "Social" },
    { name: "Music", category: "Social" },

    // Technology
    { name: "Wifi", category: "Technology" },
    { name: "Smartphone", category: "Technology" },
    { name: "Zap", category: "Technology" },
    { name: "BatteryCharging", category: "Technology" },

    // Kids & Family
    { name: "Baby", category: "Family" },
    { name: "Puzzle", category: "Family" },
    { name: "Blocks", category: "Family" },

    // Pets
    { name: "Dog", category: "Pets" },
    { name: "Cat", category: "Pets" },
    { name: "PawPrint", category: "Pets" },

    // Food & Dining
    { name: "UtensilsCrossed", category: "Dining" },
    { name: "Coffee", category: "Dining" },
    { name: "ChefHat", category: "Dining" },
    { name: "Wine", category: "Dining" },

    // Shopping & Services
    { name: "ShoppingBag", category: "Services" },
    { name: "Store", category: "Services" },
    { name: "Scissors", category: "Services" },
    { name: "Sparkles", category: "Services" },
    { name: "Heart", category: "Services" },

    // Wellness
    { name: "Leaf", category: "Wellness" },
    { name: "Moon", category: "Wellness" },
    { name: "Flame", category: "Wellness" },

    // Beach & Coastal
    { name: "Umbrella", category: "Beach" },
    { name: "Anchor", category: "Beach" },
    { name: "Sailboat", category: "Beach" },

    // Home Features
    { name: "Home", category: "Home" },
    { name: "Bed", category: "Home" },
    { name: "Bath", category: "Home" },
    { name: "Sofa", category: "Home" },
    { name: "Lamp", category: "Home" },
    { name: "Wind", category: "Home" },
    { name: "Thermometer", category: "Home" },

    // Medical
    { name: "Stethoscope", category: "Medical" },
    { name: "Cross", category: "Medical" },
    { name: "Pill", category: "Medical" },
];

interface IconPickerProps {
    value: string;
    onChange: (iconName: string) => void;
}

export function IconPicker({ value, onChange }: IconPickerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");

    const filteredIcons = useMemo(() => {
        if (!search) return AMENITY_ICONS;
        const lower = search.toLowerCase();
        return AMENITY_ICONS.filter(
            (icon) =>
                icon.name.toLowerCase().includes(lower) ||
                icon.category.toLowerCase().includes(lower)
        );
    }, [search]);

    const categories = useMemo(() => {
        const cats = new Set(filteredIcons.map((i) => i.category));
        return Array.from(cats);
    }, [filteredIcons]);

    // Get the selected icon component
    const SelectedIcon = value ? (LucideIcons as any)[value] : null;

    return (
        <div className="relative">
            {/* Selected Icon Display */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-4 py-3 border rounded-lg flex items-center gap-3 hover:border-primary transition bg-white"
            >
                {SelectedIcon ? (
                    <>
                        <SelectedIcon className="w-6 h-6 text-primary" />
                        <span className="text-sm text-text">{value}</span>
                    </>
                ) : (
                    <span className="text-sm text-text-muted">Select an icon...</span>
                )}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 mt-2 w-full bg-white border rounded-xl shadow-2xl max-h-96 overflow-hidden">
                    {/* Search */}
                    <div className="p-3 border-b sticky top-0 bg-white">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                            <input
                                type="text"
                                placeholder="Search icons..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-primary"
                            />
                            {search && (
                                <button
                                    onClick={() => setSearch("")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2"
                                >
                                    <X className="w-4 h-4 text-text-muted" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Icons Grid */}
                    <div className="overflow-y-auto max-h-72 p-3">
                        {categories.map((category) => (
                            <div key={category} className="mb-4">
                                <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
                                    {category}
                                </h4>
                                <div className="grid grid-cols-6 gap-2">
                                    {filteredIcons
                                        .filter((i) => i.category === category)
                                        .map((icon) => {
                                            const IconComponent = (LucideIcons as any)[icon.name];
                                            if (!IconComponent) return null;
                                            const isSelected = value === icon.name;
                                            return (
                                                <button
                                                    key={icon.name}
                                                    type="button"
                                                    onClick={() => {
                                                        onChange(icon.name);
                                                        setIsOpen(false);
                                                    }}
                                                    className={`p-2 rounded-lg flex items-center justify-center transition ${isSelected
                                                            ? "bg-primary text-white"
                                                            : "hover:bg-surface text-text"
                                                        }`}
                                                    title={icon.name}
                                                >
                                                    <IconComponent className="w-5 h-5" />
                                                </button>
                                            );
                                        })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// Helper component to render an icon by name
export function DynamicIcon({ name, className }: { name: string; className?: string }) {
    const IconComponent = (LucideIcons as any)[name];
    if (!IconComponent) return <span className={className}>?</span>;
    return <IconComponent className={className} />;
}
